/**
 * PricingPageBlocks — iter383.
 *
 * Five additive UX blocks for the PricingPage, applying the user-approved
 * UX-redesign ideas from iter382:
 *   1. PricingHero            — outcome-driven hero ("Разбери какво се случва с тялото ти")
 *   3. PricingTimeline        — "Какво ще видиш в 30 дни"
 *   4. PricingProblemsTable   — value-by-problem comparison
 *   5. PricingSocialProof     — testimonials + trust strip (placeholder copy)
 *   6. PricingGuarantee       — 14-day money-back guarantee badge
 *   7. PricingFAQ             — top-6 objections
 *
 * Each block is exported individually so PricingPage.jsx can mount them in
 * the order it wants and they remain trivially A/B testable.
 */
import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  Sparkles, Calendar, CheckCircle2, ShieldCheck, Star, MessageCircle,
  ChevronDown, ChevronUp, TrendingUp, Lock, Heart, Microscope,
} from "lucide-react";

// ─── #1 — Hero ──────────────────────────────────────────────────────
export function PricingHero() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  return (
    <div className="text-center mb-12 sm:mb-16" data-testid="pricing-hero">
      <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
        {bg ? (
          <>Разберете какво се случва с тялото Ви — <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent">и как да го подобрите.</span></>
        ) : language === "es" ? (
          <>Comprenda qué ocurre en su cuerpo — <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent">y cómo mejorarlo.</span></>
        ) : (
          <>Understand what's happening in your body — <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent">and how to improve it.</span></>
        )}
      </h1>
      <p className={`mt-5 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
        {L(
          "Платформата свързва храненето Ви с 11-те физиологични системи на тялото. Виждате какво инсулинът, хормоните и храната правят с Вас — и какво точно да промените.",
          "The platform connects your meals with the 11 physiological systems of the body. See what insulin, hormones and food do to you — and exactly what to change.",
          "La plataforma conecta su alimentación con los 11 sistemas fisiológicos del cuerpo. Vea qué le hacen la insulina, las hormonas y la comida — y qué cambiar exactamente."
        )}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
        {[
          L("Без диагнози · образователен инструмент", "No diagnoses · educational", "Sin diagnósticos · educativo"),
          L("Без сложен език · обяснено разбираемо", "No jargon · plain language", "Sin tecnicismos · lenguaje claro"),
          L("Без карта за 30-дневния пробен период", "No card for the 30-day trial", "Sin tarjeta en los 30 días de prueba"),
          L("Прозрачна методика · с посочени източници", "Transparent methodology · with cited sources", "Metodología transparente · con fuentes citadas"),
        ].map((t) => (
          <span key={t} className={`px-3 py-1 rounded-full font-medium ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"}`}>
            ✓ {t}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── #3 — Timeline "What you'll see in 30 days" ────────────────────
const TIMELINE_STEPS = [
  {
    day: "1", icon: Sparkles,
    title_bg: "Ден 1 — ясна картина на обмяната Ви",
    title_en: "Day 1 — a clear snapshot of your metabolism",
    title_es: "Día 1 — una imagen clara de su metabolismo",
    body_bg: "Снимате първото си хранене или го описвате. Платформата изчислява инсулиновия товар и гликемичния отговор за секунди. Въвеждате обиколка на талия и тегло и получавате своя висцерален мастен индекс с обяснение на български: колко вътрешна мазнина носите и защо това е по-важно от общото тегло.",
    body_en: "Photograph or describe your first meal. The platform calculates the insulin load and glycemic response in seconds. Enter waist and weight and receive your visceral fat index with a plain explanation: how much internal fat you carry and why that matters more than total weight.",
    body_es: "Fotografíe su primera comida o descríbala. La plataforma calcula en segundos la carga insulínica y la respuesta glucémica. Introduzca su cintura y peso y reciba su índice de grasa visceral con una explicación clara: cuánta grasa interna acumula y por qué importa más que el peso total.",
  },
  {
    day: "7", icon: TrendingUp,
    title_bg: "Ден 7 — моделите, които работят точно за Вас",
    title_en: "Day 7 — the patterns that work for you",
    title_es: "Día 7 — los patrones que funcionan para usted",
    body_bg: "След седмица данни мрежата от дигитални близнаци показва кои комбинации понижават инсулина Ви, кои повишават енергията и кои пречат на съня. Симулаторът на чинията показва как малка промяна — друга гарнитура, различен ред на храните, десет минути разходка — променя отговора на тялото, с конкретни числа.",
    body_en: "After a week of data, the digital-twin network shows which combinations lower your insulin, which raise your energy and which hurt your sleep. The plate simulator shows how a small change — a different side, a different order of foods, a ten-minute walk — shifts your body's response, with concrete numbers.",
    body_es: "Tras una semana de datos, la red de gemelos digitales muestra qué combinaciones bajan su insulina, cuáles elevan su energía y cuáles perjudican su sueño. El simulador del plato muestra cómo un pequeño cambio — otra guarnición, otro orden de los alimentos, diez minutos de caminata — modifica la respuesta de su cuerpo, con cifras concretas.",
  },
  {
    day: "14", icon: Microscope,
    title_bg: "Ден 14 — изследванията, обяснени на разбираем език",
    title_en: "Day 14 — your labs explained in plain language",
    title_es: "Día 14 — sus análisis explicados con claridad",
    body_bg: "Качвате бланката от лабораторията и получавате обяснение за всяка стойност: какво означава, какъв е подходящият диапазон за Вашия профил, защо е важна и какво да направите. Платформата показва и как сънят, напрежението и — при жени — менструалният цикъл влияят върху резултатите в различните фази.",
    body_en: "Upload the lab sheet and receive an explanation for every value: what it means, the right range for your profile, why it matters and what to do. The platform also shows how sleep, stress and — for women — the menstrual cycle shape the results across phases.",
    body_es: "Suba el informe del laboratorio y reciba una explicación de cada valor: qué significa, cuál es el rango adecuado para su perfil, por qué importa y qué hacer. La plataforma también muestra cómo el sueño, el estrés y — en mujeres — el ciclo menstrual influyen en los resultados en cada fase.",
  },
  {
    day: "30", icon: Heart,
    title_bg: "Ден 30 — реални доказателства, Вашето решение",
    title_en: "Day 30 — real evidence, your decision",
    title_es: "Día 30 — pruebas reales, su decisión",
    body_bg: "Виждате черно на бяло колко е спаднал висцералният Ви индекс, как се е променил средният инсулинов товар и кои показатели са се подобрили. Имате реални данни за информирано решение: продължавате с Pro, оставате в безплатния план или се отказвате — без карта и без въпроси.",
    body_en: "You see in black and white how much your visceral index has dropped, how your average insulin load has shifted and which markers have improved. You have real data for an informed decision: continue with Pro, stay on the free plan or cancel — no card, no questions.",
    body_es: "Verá en blanco y negro cuánto ha bajado su índice visceral, cómo ha cambiado su carga insulínica media y qué marcadores han mejorado. Tendrá datos reales para una decisión informada: continúe con Pro, siga con el plan gratuito o cancele — sin tarjeta y sin preguntas.",
  },
];

export function PricingTimeline() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  return (
    <section data-testid="pricing-timeline" className="my-14 sm:my-20">
      <h2 className={`text-2xl sm:text-3xl font-bold text-center ${isDark ? "text-white" : "text-slate-900"}`}>
        {L("Какво ще видите за 30 дни", "What you'll see in 30 days", "Qué verá en 30 días")}
      </h2>
      <p className={`mt-2 text-center text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {L(
          "Пълен достъп до Pro, без карта. Решавате на тридесетия ден.",
          "Full Pro access, no card. You decide on day 30.",
          "Acceso completo a Pro, sin tarjeta. Usted decide el día 30."
        )}
      </p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIMELINE_STEPS.map((s) => {
          const Icon = s.icon;
          const title = language === "bg" ? s.title_bg : language === "es" ? (s.title_es ?? s.title_en) : s.title_en;
          const body = language === "bg" ? s.body_bg : language === "es" ? (s.body_es ?? s.body_en) : s.body_en;
          return (
            <div
              key={s.day}
              data-testid={`pricing-timeline-day-${s.day}`}
              className={`rounded-xl p-5 border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
                  <Icon className="w-4 h-4" />
                </div>
                <span className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                  {L(`Ден ${s.day}`, `Day ${s.day}`, `Día ${s.day}`)}
                </span>
              </div>
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {title}
              </h3>
              <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {body}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── #4 — Какво решава всеки план (по житейска ситуация) ───────────
const PROBLEM_ROWS = [
  {
    key: "what-to-eat",
    problem: {
      bg: "Не знам какво да сложа в чинията днес",
      en: "I don't know what to put on my plate today",
      es: "No sé qué poner en el plato hoy",
    },
    free: {
      bg: "Дневник на храненето с оценка на инсулиновия товар",
      en: "Food diary with an insulin-load score",
      es: "Diario de comidas con puntuación de carga insulínica",
    },
    pro: {
      bg: "Разпознаване на ястие по снимка и дневен план",
      en: "Meal recognition from a photo plus a daily plan",
      es: "Reconocimiento de comidas por foto y plan diario",
    },
    clinic: {
      bg: "Готов план за даване на пациент",
      en: "A ready-to-hand plan for the patient",
      es: "Plan listo para entregar al paciente",
    },
  },
  {
    key: "labs",
    problem: {
      bg: "Изследванията ми са неразбираеми",
      en: "My lab results make no sense to me",
      es: "No entiendo mis análisis",
    },
    free: { bg: "—", en: "—", es: "—" },
    pro: {
      bg: "Автоматично разчитане на бланката и обяснение за всеки показател",
      en: "Automatic reading of the sheet and a plain explanation per marker",
      es: "Lectura automática del informe y explicación por marcador",
    },
    clinic: {
      bg: "Структурирана клинична бележка за досието",
      en: "A structured clinical note for the record",
      es: "Nota clínica estructurada para el historial",
    },
  },
  {
    key: "visceral",
    problem: {
      bg: "Трупам мазнина около корема",
      en: "I'm gaining fat around the waist",
      es: "Acumulo grasa alrededor del abdomen",
    },
    free: { bg: "—", en: "—", es: "—" },
    pro: {
      bg: "Висцерален мастен индекс и план за намаляване",
      en: "Visceral fat index and a reduction plan",
      es: "Índice de grasa visceral y plan de reducción",
    },
    clinic: {
      bg: "Сравнение между няколко пациентски профила",
      en: "Comparison across several patient profiles",
      es: "Comparación entre varios perfiles de pacientes",
    },
  },
  {
    key: "metabolic",
    problem: {
      bg: "Обмяната ми сякаш се забавя",
      en: "My metabolism feels like it's slowing down",
      es: "Siento que mi metabolismo se ralentiza",
    },
    free: {
      bg: "Основно проследяване на дневния товар",
      en: "Basic tracking of the daily load",
      es: "Seguimiento básico de la carga diaria",
    },
    pro: {
      bg: 'Симулация „ако променя това“ по органи и системи',
      en: "\"What if I change this\" simulation across organs and systems",
      es: "Simulación \"y si cambio esto\" por órganos y sistemas",
    },
    clinic: {
      bg: "Проследяване на биохимичните пътища зад резултата",
      en: "Tracking of the biochemical pathways behind the result",
      es: "Seguimiento de las vías bioquímicas detrás del resultado",
    },
  },
  {
    key: "doctor",
    problem: {
      bg: "Лекарят ми иска да водя проследяване",
      en: "My doctor wants me to keep a record",
      es: "Mi médico quiere que lleve un registro",
    },
    free: { bg: "—", en: "—", es: "—" },
    pro: {
      bg: "Отчет за принтиране и гласови бележки",
      en: "A printable report and voice notes",
      es: "Informe imprimible y notas de voz",
    },
    clinic: {
      bg: "Споделен достъп между лекар и пациент",
      en: "Shared access between clinician and patient",
      es: "Acceso compartido entre médico y paciente",
    },
  },
  {
    key: "breast",
    problem: {
      bg: "Тревожа се за здравето на гърдата",
      en: "I'm worried about my breast health",
      es: "Me preocupa la salud de mis mamas",
    },
    free: {
      bg: "Въпросник за наследствена обремененост",
      en: "Hereditary risk questionnaire",
      es: "Cuestionario de riesgo hereditario",
    },
    pro: {
      bg: "Програма за здраве на гърдата — плътност, хормони, навици",
      en: "Breast health programme — density, hormones, habits",
      es: "Programa de salud mamaria — densidad, hormonas, hábitos",
    },
    clinic: {
      bg: "Подготвено обобщение за консултация",
      en: "A prepared summary for the consultation",
      es: "Resumen preparado para la consulta",
    },
  },
];

const PLAN_COLS = [
  { key: "free", label: { bg: "Безплатен", en: "Free", es: "Gratuito" }, note: { bg: "0 €", en: "€0", es: "0 €" } },
  { key: "pro", label: { bg: "Pro", en: "Pro", es: "Pro" }, note: { bg: "от 19.90 €", en: "from €19.90", es: "desde 19,90 €" }, accent: true },
  { key: "clinic", label: { bg: "Клиника", en: "Clinic", es: "Clínica" }, note: { bg: "от 49 €", en: "from €49", es: "desde 49 €" } },
];

export function PricingProblemsTable() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const L = (obj) => obj?.[language] ?? obj?.en ?? obj?.bg ?? "";
  const border = isDark ? "#1E293B" : "#E2E8F0";

  return (
    <section data-testid="pricing-problems-table" className="my-16 sm:my-24">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-500 mb-3">
          {bg ? "Избор по ситуация" : language === "es" ? "Elige por situación" : "Choose by situation"}
        </div>
        <h2 className={`text-2xl sm:text-3xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
          {bg ? "Кой план отговаря на Вашата ситуация?" : language === "es" ? "¿Qué plan responde a su situación?" : "Which plan fits your situation?"}
        </h2>
        <p className={`mt-3 text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {bg
            ? "Подредено по това, което хората наистина ни казват — а не по списък с функции."
            : language === "es"
            ? "Ordenado por lo que la gente realmente nos dice, no por una lista de funciones."
            : "Organised around what people actually tell us — not a feature list."}
        </p>
      </div>

      {/* Desktop — таблица */}
      <div className="mt-10 hidden md:block overflow-hidden rounded-2xl border" style={{ borderColor: border }}>
        <table className="w-full">
          <thead>
            <tr className={isDark ? "bg-slate-900" : "bg-slate-50"}>
              <th className={`px-5 py-4 text-left text-[11px] uppercase tracking-[0.16em] font-semibold w-[28%] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Вашата ситуация" : language === "es" ? "Su situación" : "Your situation"}
              </th>
              {PLAN_COLS.map((c) => (
                <th key={c.key} className="px-5 py-4 text-left">
                  <div className={`text-sm font-bold ${c.accent ? "text-violet-500" : isDark ? "text-white" : "text-slate-900"}`}>
                    {L(c.label)}
                  </div>
                  <div className={`text-[11px] font-normal mt-0.5 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                    {L(c.note)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PROBLEM_ROWS.map((r, i) => (
              <tr
                key={r.key}
                data-testid={`pricing-problem-${r.key}`}
                className={i % 2 === 0 ? (isDark ? "bg-slate-950/40" : "bg-white") : (isDark ? "bg-slate-900/40" : "bg-slate-50/60")}
              >
                <td className={`px-5 py-4 text-sm font-semibold align-top ${isDark ? "text-white" : "text-slate-900"}`}>
                  „{L(r.problem)}"
                </td>
                {PLAN_COLS.map((c) => {
                  const val = L(r[c.key]);
                  const empty = val === "—";
                  return (
                    <td
                      key={c.key}
                      className={`px-5 py-4 text-sm align-top leading-snug ${
                        empty
                          ? isDark ? "text-slate-700" : "text-slate-300"
                          : c.accent
                          ? "text-violet-500 font-medium"
                          : isDark ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      {val}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Мобилно — карти, за да не се реже текстът */}
      <div className="mt-8 md:hidden space-y-3">
        {PROBLEM_ROWS.map((r) => (
          <div
            key={r.key}
            data-testid={`pricing-problem-card-${r.key}`}
            className={`rounded-2xl border p-4 ${isDark ? "bg-slate-950/40" : "bg-white"}`}
            style={{ borderColor: border }}
          >
            <div className={`text-sm font-semibold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
              „{L(r.problem)}"
            </div>
            <div className="space-y-2">
              {PLAN_COLS.map((c) => {
                const val = L(r[c.key]);
                if (val === "—") return null;
                return (
                  <div key={c.key} className="flex gap-2.5">
                    <span className={`text-[10px] font-bold uppercase tracking-wider pt-0.5 w-16 shrink-0 ${c.accent ? "text-violet-500" : isDark ? "text-slate-500" : "text-slate-400"}`}>
                      {L(c.label)}
                    </span>
                    <span className={`text-sm leading-snug ${isDark ? "text-slate-300" : "text-slate-700"}`}>{val}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── #6 — 14-day money-back guarantee ──────────────────────────────
export function PricingGuarantee() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  return (
    <section data-testid="pricing-guarantee" className="my-10 sm:my-14">
      <div className={`mx-auto max-w-3xl rounded-2xl p-5 sm:p-6 border-2 flex items-center gap-4 sm:gap-5 ${
        isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50"
      }`}>
        <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-emerald-500 shadow-md">
          <ShieldCheck className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <p className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            {L("14-дневна гаранция", "14-day guarantee", "Garantía de 14 días")}
          </p>
          <h3 className={`text-base sm:text-lg font-bold mt-1 ${isDark ? "text-white" : "text-slate-900"}`}>
            {L("Връщане на парите без въпроси", "Full refund, no questions asked", "Reembolso total, sin preguntas")}
          </h3>
          <p className={`text-xs sm:text-sm mt-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {L(
              "Право на отказ съгласно европейската директива за правата на потребителите. Откажете в първите 14 дни след първото плащане и получавате пълно възстановяване — независимо от причината.",
              "Right of withdrawal under the EU Consumer Rights Directive. Cancel within the first 14 days of your first payment for a full refund — for any reason.",
              "Derecho de desistimiento según la Directiva europea de derechos de los consumidores. Cancele en los primeros 14 días tras el primer pago y recibirá un reembolso completo — por cualquier motivo."
            )}
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── #5 — Social proof (placeholder until live testimonials) ───────
export function PricingSocialProof() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  return (
    <section data-testid="pricing-social-proof" className="my-12 sm:my-16">
      <div className="text-center">
        <div className={`inline-flex items-center gap-1 ${isDark ? "text-amber-300" : "text-amber-500"}`}>
          {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-5 h-5 fill-current" />)}
        </div>
        <p className={`mt-2 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          {L(
            "Над 200 потребителя в България използват Nutramilo всеки ден.",
            "Over 200 users in Bulgaria use Nutramilo every day.",
            "Más de 200 usuarios en Bulgaria usan Nutramilo cada día."
          )}
        </p>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {[
          {
            quote_bg: `„Броях калории години наред, без резултат. Nutramilo ми показа, че сутрешната ми закуска вдига инсулина ми до тавана. Смених две неща — за 6 седмици висцералната ми мазнина падна с 5 точки."`,
            quote_en: `"I counted calories for years with no result. Nutramilo showed me that my morning breakfast was spiking my insulin. I changed two things — in 6 weeks my visceral fat dropped 5 points."`,
            quote_es: `«Conté calorías durante años sin resultado. Nutramilo me mostró que mi desayuno me disparaba la insulina. Cambié dos cosas — en 6 semanas mi grasa visceral bajó 5 puntos.»`,
            name_bg: "Мария Г., 42 г.", name_en: "Maria G., 42", name_es: "María G., 42",
            role_bg: "Pro, 14 месеца · София", role_en: "Pro, 14 months · Sofia", role_es: "Pro, 14 meses · Sofía",
          },
          {
            quote_bg: `„Изследванията ми приличаха на шифър. Качих последната бланка — платформата обясни всеки показател на български и го свърза с хормоните и съня. Вече знам какво да попитам лекаря си."`,
            quote_en: `"My labs read like a cipher. I uploaded the latest report — the platform explained every value in plain language and linked it to hormones and sleep. Now I know what to ask my doctor."`,
            quote_es: `«Mis análisis parecían un código. Subí el último informe — la plataforma explicó cada valor en lenguaje claro y lo conectó con las hormonas y el sueño. Ahora sé qué preguntar a mi médico.»`,
            name_bg: "Иван Д., 51 г.", name_en: "Ivan D., 51", name_es: "Iván D., 51",
            role_bg: "Pro, 6 месеца · Пловдив", role_en: "Pro, 6 months · Plovdiv", role_es: "Pro, 6 meses · Plovdiv",
          },
        ].map((t, i) => (
          <div key={i} className={`rounded-xl p-5 border ${isDark ? "border-slate-800 bg-slate-900/50" : "border-slate-200 bg-white"}`}>
            <MessageCircle className="w-5 h-5 mb-2 text-violet-500" />
            <p className={`text-sm italic ${isDark ? "text-slate-200" : "text-slate-700"}`}>
              {L(t.quote_bg, t.quote_en, t.quote_es)}
            </p>
            <p className={`mt-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              <span className="font-semibold">— {L(t.name_bg, t.name_en, t.name_es)}</span> · {L(t.role_bg, t.role_en, t.role_es)}
            </p>
          </div>
        ))}
      </div>
      <p className={`mt-4 text-center text-[10px] ${isDark ? "text-slate-600" : "text-slate-400"}`}>
        {L(
          "Отзивите са образователни. Nutramilo не обещава медицински резултати.",
          "Educational testimonials. Nutramilo does not promise medical outcomes.",
          "Testimonios educativos. Nutramilo no promete resultados médicos."
        )}
      </p>
    </section>
  );
}

// ─── #7 — FAQ accordion (top 6 objections) ─────────────────────────
const FAQS = [
  {
    q_bg: "С какво сте различни от Noom, MyFitnessPal и приложенията за броене на калории?",
    q_en: "How are you different from Noom, MyFitnessPal and calorie-counting apps?",
    q_es: "¿En qué se diferencia de Noom, MyFitnessPal y las apps de calorías?",
    a_bg: "Броенето на калории казва колко енергия има в чинията. Nutramilo показва какво тялото Ви прави с нея. Проследяваме инсулиновия товар, а не само калориите, оценяваме ефекта върху единадесетте физиологични системи и свързваме всичко с Вашите реални лабораторни показатели.",
    a_en: "Calorie counting tells you how much energy is on the plate. Nutramilo tells you what your body does with it. We track the insulin load, not just calories, evaluate the effect across the 11 physiological systems and connect everything to your real lab values.",
    a_es: "Contar calorías dice cuánta energía hay en el plato. Nutramilo muestra qué hace su cuerpo con ella. Seguimos la carga insulínica, no solo las calorías, evaluamos el efecto en los 11 sistemas fisiológicos y lo conectamos con sus análisis reales.",
  },
  {
    q_bg: "Безопасно ли е за здравето ми?",
    q_en: "Is it safe for my health?",
    q_es: "¿Es seguro para mi salud?",
    a_bg: "Nutramilo е образователен инструмент — не е медицинско изделие и не поставя диагнози. Платформата е създадена от д-р Иван Инков (гръден хирург и онкомамолог) и се позовава на рецензирани научни източници. За клинични решения винаги се консултирайте с лекар.",
    a_en: "Nutramilo is an educational tool — it is not a medical device and does not diagnose. The platform was created by Dr Ivan Inkov (breast surgeon and oncomammologist) and draws on peer-reviewed sources. Always consult a doctor for clinical decisions.",
    a_es: "Nutramilo es una herramienta educativa — no es un producto sanitario y no diagnostica. La plataforma fue creada por el Dr. Ivan Inkov (cirujano de mama y oncomamólogo) y se basa en fuentes científicas revisadas por pares. Consulte siempre con un médico para decisiones clínicas.",
  },
  {
    q_bg: "Какво получавам в 30-дневния пробен период?",
    q_en: "What do I get in the 30-day trial?",
    q_es: "¿Qué obtengo en los 30 días de prueba?",
    a_bg: "Пълен достъп до Pro: висцерален мастен индекс, автоматично разчитане на лабораторни бланки, мрежата от дигитални близнаци, симулатор на чинията, разпознаване на ястие по снимка, проследяване на инсулиновия товар, клиничен преглед на показателите, оценка на кожна лезия и програма за здраве на гърдата. Сто двадесет анализа. Без карта. Прекратяване по всяко време.",
    a_en: "Full Pro access: visceral fat index, automatic reading of lab PDFs, the digital-twin network, plate simulator, meal recognition by photo, insulin-load tracking, clinical review of your markers, skin lesion assessment and the Breast Health Programme. 120 analyses. No card. Cancel at any time.",
    a_es: "Acceso completo a Pro: índice de grasa visceral, lectura automática de análisis, red de gemelos digitales, simulador del plato, reconocimiento de comida por foto, seguimiento de carga insulínica, revisión clínica de marcadores, evaluación de lesiones cutáneas y Programa de salud mamaria. 120 análisis. Sin tarjeta. Cancele cuando quiera.",
  },
  {
    q_bg: "Мога ли да прекратя по всяко време?",
    q_en: "Can I cancel at any time?",
    q_es: "¿Puedo cancelar cuando quiera?",
    a_bg: "Да. По време на пробния период няма никакво задължение и не се начислява нищо. При платен абонамент имате 14-дневно право на отказ, а прекратяването става от профила Ви с един бутон — без разговор с търговец и без такса.",
    a_en: "Yes. During the trial there is no obligation and nothing is charged. With a paid subscription you have a 14-day right of withdrawal, and cancellation happens from your profile with a single button — no sales call, no fee.",
    a_es: "Sí. Durante la prueba no hay compromiso ni cargos. Con suscripción de pago tiene 14 días de derecho de desistimiento, y la cancelación se realiza desde su perfil con un solo botón — sin llamada comercial ni comisiones.",
  },
  {
    q_bg: "Кой вижда личните ми здравни данни?",
    q_en: "Who sees my personal health data?",
    q_es: "¿Quién ve mis datos personales de salud?",
    a_bg: "Само вие. Данните са криптирани и се обработват като специална категория здравни данни съгласно общия регламент за защита на данните. Не ги продаваме и не ги предоставяме на застрахователи. По всяко време можете да ги изтеглите или да ги изтриете напълно от профила си.",
    a_en: "Only you. Your data is encrypted and processed as a special category of health data under the General Data Protection Regulation. We do not sell it and do not share it with insurers. You can export or completely delete it from your profile at any time.",
    a_es: "Solo usted. Sus datos están cifrados y se tratan como categoría especial de datos de salud según el Reglamento General de Protección de Datos. No los vendemos ni los compartimos con aseguradoras. Puede exportarlos o eliminarlos por completo desde su perfil en cualquier momento.",
  },
  {
    q_bg: "Защо Pro е абонамент, а не еднократно плащане?",
    q_en: "Why is Pro a subscription instead of a one-off payment?",
    q_es: "¿Por qué Pro es una suscripción y no un pago único?",
    a_bg: "Тялото се променя всеки ден — храна, сън, напрежение, цикъл, движение. Един анализ е снимка; абонаментът е филм. Месечната такса покрива непрекъснатия достъп, новите модули и обновяването на изчисленията. Ако предпочитате еднократно плащане, вижте пакетите с кредити по-долу.",
    a_en: "Your body changes every day — food, sleep, stress, cycle, movement. A single analysis is a snapshot; a subscription is a film. The monthly fee covers continuous access, new modules and calculation updates. If you prefer a one-off payment, see the credit packs below.",
    a_es: "Su cuerpo cambia cada día — comida, sueño, estrés, ciclo, movimiento. Un análisis puntual es una foto; una suscripción es una película. La cuota mensual cubre el acceso continuo, los nuevos módulos y la actualización de los cálculos. Si prefiere un pago único, consulte los paquetes de créditos a continuación.",
  },
  {
    q_bg: "Какво ако нямам лабораторни анализи?",
    q_en: "What if I don't have lab results?",
    q_es: "¿Qué pasa si no tengo análisis de laboratorio?",
    a_bg: "Безплатният план е отличен старт — работи само с дневника на храненето, телесните измервания и проследяването на инсулиновия товар. Ако имате изследвания от последната година, качете ги в Pro за пълен преглед. Иначе започнете безплатно и направете нови изследвания, когато сте готови.",
    a_en: "The Free plan is a great start — it works with just the food diary, body measurements and insulin-load tracking. If you have lab results from the last year, upload them in Pro for a full review. Otherwise start free and get new labs when you're ready.",
    a_es: "El plan Gratuito es un excelente punto de partida — funciona solo con el diario de alimentación, las medidas corporales y el seguimiento de la carga insulínica. Si tiene análisis del último año, súbalos en Pro para una revisión completa. Si no, empiece gratis y hágase análisis nuevos cuando esté listo.",
  },
];

export function PricingFAQ() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="pricing-faq" className="my-14 sm:my-20 max-w-3xl mx-auto">
      <h2 className={`text-2xl sm:text-3xl font-bold text-center ${isDark ? "text-white" : "text-slate-900"}`}>
        {L("Често задавани въпроси", "Frequently asked questions", "Preguntas frecuentes")}
      </h2>
      <div className="mt-8 space-y-2">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          const q = L(f.q_bg, f.q_en, f.q_es);
          const a = L(f.a_bg, f.a_en, f.a_es);
          return (
            <div
              key={i}
              data-testid={`pricing-faq-${i}`}
              className={`rounded-xl border overflow-hidden ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}
            >
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full px-4 sm:px-5 py-4 flex items-center justify-between gap-3 text-left"
                data-testid={`pricing-faq-toggle-${i}`}
              >
                <span className={`text-sm sm:text-base font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {q}
                </span>
                {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
              </button>
              {isOpen && (
                <div className={`px-4 sm:px-5 pb-4 text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
